import { useEffect, useRef, useState } from "react";
import useMetaPixel from "./useMetaPixel";
import { isValidEmail } from "@/utils/formValidator";
import { pushEvent } from "@/utils/gtm";
import { cn } from "@/lib/utils";

const useNewslettersSubscription = (
  setErrorMsg: React.Dispatch<React.SetStateAction<string>>,
  setSuccessMsg: React.Dispatch<React.SetStateAction<string>>,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  klaviyoMetricType?: string,
) => {
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const baseApiUrl = import.meta.env.VITE_BASE_API_URL;
  const metaPixel = useMetaPixel();
  const metaPixelRef = useRef(metaPixel);

  const isSleepGuideAd =
    new URLSearchParams(window.location.search).get("type") === "ad";

  useEffect(() => {
    metaPixelRef.current = metaPixel;
  });

  useEffect(() => {
    if (isSleepGuideAd) {
      pushEvent("sleep_guide_started");
      metaPixelRef.current.trackSleepGuideStarted(window.location.href);
    }
  }, [isSleepGuideAd]);

  const clearMessages = () => {
    setErrorMsg("");
    setSuccessMsg("");
  };

  const isValid = (email: string, isConfirmationMandatory = true) => {
    if (email.trim() === "") return "Užpildykite el. pašto adresą";
    else if (!isValidEmail(email.trim()))
      return "Nekorektiškas el. pašto adresas";
    else if (isConfirmationMandatory && !isConfirmed)
      return "Patvirtinkite, kad norite gauti naujienlaiškius.";
  };

  const subscribe = async (email: string) => {
    clearMessages();

    const errorMessage = isValid(email);
    if (errorMessage) {
      setErrorMsg(errorMessage);
    } else {
      setIsLoading(true);

      try {
        const res = await fetch(baseApiUrl + "/subscriptions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            email,
            metric: klaviyoMetricType,
            website: "",
          }),
        });

        const data = (await res.json()) as { message?: string };
        if (res.ok) {
          metaPixel.trackSubscribeToNewsletter();
          if (isSleepGuideAd) {
            metaPixel.trackSleepGuideEnded(window.location.href);
            pushEvent("sleep_guide_ended");
          } else {
            pushEvent("newsletter_subscribe");
          }
          if (data.message) {
            setSuccessMsg(data.message);
          } else {
            setSuccessMsg("Sėkmingai užsiprenumeravote mūsų naujienlaiškį.");
          }
        } else if (res.status === 500) {
          setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
        } else if (res.status === 422 && data.message) {
          setErrorMsg(data.message);
        } else {
          setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
        }
      } catch {
        setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const onChangeIsConfirmed: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    clearMessages();
    setIsConfirmed(e.target.checked);
  };

  return {
    subscribe,
    isValid,
    ConfirmationCheckbox: ({
      className,
      inputClassName,
    }: {
      className?: string;
      inputClassName?: string;
    }) => (
      <label
        className={cn(
          "form-check-label flex flex-row items-center gap-4 text-sm",
          className,
        )}
        style={{ fontSize: ".800rem" }}
      >
        <input
          className={cn("h-4 w-4", inputClassName)}
          type="checkbox"
          id="confirmationCheckbox"
          name="confirmation"
          checked={isConfirmed}
          onChange={onChangeIsConfirmed}
        />
        Sutinku gauti el. laiškus su naujienomis, patarimais ir specialiais
        pasiūlymais.
      </label>
    ),
  };
};

export default useNewslettersSubscription;
